import * as Sentry from '@sentry/react'
import posthog from 'posthog-js'

export interface CustomerIoAnalytics {
	track(eventName: string, properties?: Record<string, unknown>): unknown
}

type LogLevel = 'debug' | 'info' | 'warning' | 'error' | 'fatal'
type ConsoleMethod = 'debug' | 'info' | 'warn' | 'error'

export interface LogOptions {
	/** Breadcrumb category (e.g., 'api', 'auth', 'validation') */
	category?: string
	/**
	 * Additional context data. Included in breadcrumbs and PostHog events.
	 * Use for values, IDs, and debugging info.
	 */
	data?: Record<string, unknown>
	/**
	 * Sentry tags for filtering/searching in the Sentry UI.
	 * Use for dimensions you'll want to filter by (feature, action, etc).
	 * Note: For breadcrumbs, tags are merged into data — avoid duplicate keys.
	 */
	tags?: Record<string, string>
	/** Skip sending to analytics targets for this event */
	skipAnalytics?: boolean
}

interface LoggerConfig {
	devMode?: boolean
	enableConsoleLogs?: boolean
}

const config: LoggerConfig = {
	devMode: import.meta.env.DEV,
	enableConsoleLogs: false,
}

// Registry to hold Customer.io analytics instance
let customerIo: CustomerIoAnalytics | null = null

// Demo organisations are excluded from PostHog so sandbox activity doesn't muddy
// real customer analytics. Customer.io is unaffected — it still needs demo-org
// events so its emails/journeys stay testable.
let isDemoOrganisation = false

const consoleMethods: Record<LogLevel, ConsoleMethod> = {
	debug: 'debug',
	info: 'info',
	warning: 'warn',
	error: 'error',
	fatal: 'error',
}

function logToConsole(level: LogLevel, message: string, data?: Record<string, unknown>) {
	const method = consoleMethods[level]
	if (data && Object.keys(data).length > 0) {
		console[method](`[${level.toUpperCase()}]`, message, data)
	} else {
		console[method](`[${level.toUpperCase()}]`, message)
	}
}

export const logger = {
	/**
	 * Configure logger behavior
	 */
	configure(options: LoggerConfig) {
		Object.assign(config, options)
	},

	/**
	 * Set the Customer.io analytics instance.
	 * Called by the app's Customer.io provider when initialized.
	 *
	 * @internal
	 */
	setCustomerIo(analytics: CustomerIoAnalytics | null) {
		customerIo = analytics
	},

	/**
	 * Mark whether the currently active organisation is a demo organisation.
	 * While true, PostHog capture/group calls are skipped; Sentry and
	 * Customer.io are unaffected. Called when entering a company route.
	 */
	setDemoOrganisation(isDemo: boolean) {
		isDemoOrganisation = isDemo
	},

	// ─────────────────────────────────────────────────────────────
	// Logging by level
	// ─────────────────────────────────────────────────────────────

	/**
	 * Log a debug message
	 *
	 * - Console: ✓
	 * - Sentry breadcrumb: ✗
	 * - Sentry event: ✗
	 * - PostHog: ✗
	 */
	debug(message: string, data?: Record<string, unknown>) {
		if (config.devMode || config.enableConsoleLogs) {
			logToConsole('debug', message, data)
		}
	},

	/**
	 * Log an informational message
	 *
	 * - Console: ✓
	 * - Sentry breadcrumb: ✓
	 * - Sentry event: ✗
	 * - PostHog: ✗
	 */
	info(message: string, options: LogOptions = {}) {
		if (config.devMode || config.enableConsoleLogs) {
			logToConsole('info', message, options.data)
		}

		if (!config.devMode) {
			Sentry.addBreadcrumb({
				message,
				category: options.category ?? 'app',
				level: 'info',
				timestamp: Date.now() / 1000,
				data: { ...options.tags, ...options.data },
			})
		}
	},

	/**
	 * Log a warning
	 *
	 * - Console: ✓
	 * - Sentry breadcrumb: ✓
	 * - Sentry event: ✗
	 * - PostHog: ✓
	 */
	warn(message: string, options: LogOptions = {}) {
		if (config.devMode || config.enableConsoleLogs) {
			logToConsole('warning', message, options.data)
		}

		if (!config.devMode) {
			Sentry.addBreadcrumb({
				message,
				category: options.category ?? 'app',
				level: 'warning',
				timestamp: Date.now() / 1000,
				data: { ...options.tags, ...options.data },
			})

			if (!options.skipAnalytics && !isDemoOrganisation) {
				posthog.capture('warning', {
					message,
					category: options.category,
					...options.tags,
					...options.data,
				})
			}
		}
	},

	/**
	 * Capture an error/exception
	 *
	 * - Console: ✓
	 * - Sentry breadcrumb: ✗
	 * - Sentry event: ✓ (captureException)
	 * - PostHog: ✓
	 */
	error(error: unknown, options: LogOptions = {}) {
		if (config.devMode || config.enableConsoleLogs) {
			console.error('[ERROR]', error, { ...options.tags, ...options.data })
		}

		if (!config.devMode) {
			Sentry.captureException(error, {
				level: 'error',
				tags: options.tags,
				extra: options.data,
			})

			if (!options.skipAnalytics && !isDemoOrganisation) {
				posthog.capture('error', {
					message: error instanceof Error ? error.message : String(error),
					errorName: error instanceof Error ? error.name : undefined,
					...options.tags,
					...options.data,
				})
			}
		}
	},

	/**
	 * Capture a fatal error
	 *
	 * - Console: ✓
	 * - Sentry breadcrumb: ✗
	 * - Sentry event: ✓ (captureException)
	 * - PostHog: ✓
	 */
	fatal(error: unknown, options: LogOptions = {}) {
		if (config.devMode || config.enableConsoleLogs) {
			console.error('[FATAL]', error, { ...options.tags, ...options.data })
		}

		if (!config.devMode) {
			Sentry.captureException(error, {
				level: 'fatal',
				tags: options.tags,
				extra: options.data,
			})

			if (!options.skipAnalytics && !isDemoOrganisation) {
				posthog.capture('fatal_error', {
					message: error instanceof Error ? error.message : String(error),
					errorName: error instanceof Error ? error.name : undefined,
					...options.tags,
					...options.data,
				})
			}
		}
	},

	// ─────────────────────────────────────────────────────────────
	// PostHog: Analytics & events
	// ─────────────────────────────────────────────────────────────

	/**
	 * Track a product analytics event
	 *
	 * - Console: ✓
	 * - Sentry breadcrumb: ✓
	 * - Sentry event: ✗
	 * - PostHog: ✓
	 * - Customer.io: ✓
	 */
	track(eventName: string, options: LogOptions = {}) {
		if (config.devMode || config.enableConsoleLogs) {
			console.info('[TRACK]', eventName, options.data)
		}

		if (!config.devMode) {
			if (!isDemoOrganisation) {
				posthog.capture(eventName, options.data)
			}
			customerIo?.track(eventName, options.data)

			Sentry.addBreadcrumb({
				message: eventName,
				category: options.category ?? 'analytics',
				level: 'info',
				timestamp: Date.now() / 1000,
				data: options.data,
			})
		}
	},

	// ─────────────────────────────────────────────────────────────
	// Shared: User identity (both services)
	// ─────────────────────────────────────────────────────────────

	/**
	 * Identify user across both Sentry and PostHog.
	 * Pass `null` to clear identity (e.g., on logout).
	 *
	 * - Sentry: ✓
	 * - PostHog: ✓
	 */
	identify(user: { id: string; email?: string; username?: string; [key: string]: unknown } | null) {
		if (config.devMode) {
			console.debug('[IDENTIFY]', user)
			return
		}

		if (user) {
			Sentry.setUser({ id: user.id, email: user.email, username: user.username })
			posthog.identify(user.id, user)
		} else {
			Sentry.setUser(null)
			posthog.reset()
		}
	},

	/**
	 * Associate the user with a group (e.g., company/organization).
	 * Pass `null` to clear the group.
	 *
	 * - Sentry: ✓ (as context + tag)
	 * - PostHog: ✓
	 */
	setGroup(
		groupType: string,
		groupId: string | null,
		properties?: Record<string, unknown>,
	) {
		if (config.devMode) {
			console.debug('[GROUP]', groupType, groupId, properties)
			return
		}

		if (groupId) {
			if (!isDemoOrganisation) {
				posthog.group(groupType, groupId, properties)
			}
			Sentry.setTag(groupType, groupId)
			Sentry.setContext(groupType, { id: groupId, ...properties })
		} else {
			if (!isDemoOrganisation) {
				const currentGroups = posthog.getGroups()
				posthog.resetGroups()
				for (const [type, key] of Object.entries(currentGroups)) {
					if (type !== groupType && key) {
						posthog.group(type, String(key))
					}
				}
			}
			Sentry.getCurrentScope().setTag(groupType, undefined)
			Sentry.setContext(groupType, null)
		}
	},

	// ─────────────────────────────────────────────────────────────
	// Context & tags (Sentry only)
	// ─────────────────────────────────────────────────────────────

	/**
	 * Set a tag that will be applied to all subsequent Sentry events.
	 * Best for session-level context (user plan, app version, etc).
	 *
	 * - Sentry: ✓
	 * - PostHog: ✗
	 */
	setTag(key: string, value: string) {
		if (config.devMode) {
			console.debug('[TAG]', key, value)
			return
		}
		Sentry.setTag(key, value)
	},

	/**
	 * Set extra context data for all subsequent Sentry events.
	 *
	 * - Sentry: ✓
	 * - PostHog: ✗
	 */
	setContext(name: string, context: Record<string, unknown> | null) {
		if (config.devMode) {
			console.debug('[CONTEXT]', name, context)
			return
		}
		Sentry.setContext(name, context)
	},

	// ─────────────────────────────────────────────────────────────
	// PostHog: Super properties
	// ─────────────────────────────────────────────────────────────

	/**
	 * Register super properties that attach to all subsequent PostHog events
	 * (including $pageview). Call with updated values to overwrite.
	 *
	 * - PostHog: ✓
	 */
	register(properties: Record<string, unknown>) {
		if (config.devMode) {
			console.debug('[REGISTER]', properties)
			return
		}
		posthog.register(properties)
	},

	/**
	 * Remove a previously registered super property.
	 *
	 * - PostHog: ✓
	 */
	unregister(property: string) {
		if (config.devMode) {
			console.debug('[UNREGISTER]', property)
			return
		}
		posthog.unregister(property)
	},

	// ─────────────────────────────────────────────────────────────
	// Utilities
	// ─────────────────────────────────────────────────────────────

	/**
	 * Wrap an async operation with error capturing.
	 * Errors are logged via `error()` and re-thrown.
	 */
	async wrap<T>(operation: () => Promise<T>, options?: LogOptions): Promise<T> {
		try {
			return await operation()
		} catch (error) {
			this.error(error, options)
			throw error
		}
	},
}
